import { useState, useEffect } from 'react';
import { useAuthStore } from '../../hooks/useAuth';
import { AlertTriangle, ShieldOff, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────
//  BlackoutCreateModal — create a dock blackout window
//  V1.3B: Two-step confirm flow with conflict detection
// ─────────────────────────────────────────────────────

interface Dock {
    id: string;
    dockNumber: string;
}

interface ConflictAppointment {
    appointmentId: string;
    carrierName: string | null;
    startAt: string;
    endAt: string;
    status: string;
    dockNumber: string | null;
}

interface BlackoutCreateModalProps {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
    docks: Dock[];
    preselectedDockId?: string | null;
}

const REASON_PRESETS = [
    'Maintenance',
    'Lunch / Break',
    'Inventory Count',
    'Cleaning / Sanitation',
    'Safety Inspection',
    'Receiving Pause',
    'Other',
];

export function BlackoutCreateModal({ open, onClose, onCreated, docks, preselectedDockId }: BlackoutCreateModalProps) {
    const { token } = useAuthStore();

    // Form state
    const [selectedDockId, setSelectedDockId] = useState<string>('');
    const [applyToAll, setApplyToAll] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('08:00');
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('17:00');
    const [reason, setReason] = useState('Maintenance');
    const [customReason, setCustomReason] = useState('');

    // Conflict state
    const [conflicts, setConflicts] = useState<ConflictAppointment[]>([]);
    const [showConflicts, setShowConflicts] = useState(false);
    const [conflictWarning, setConflictWarning] = useState('');

    const [loading, setLoading] = useState(false);

    // V1.3B: Initialize from preselectedDockId when modal opens
    useEffect(() => {
        if (open && preselectedDockId) {
            setSelectedDockId(preselectedDockId);
            setApplyToAll(false);
        } else if (open && !preselectedDockId) {
            setSelectedDockId('');
            setApplyToAll(false);
        }
    }, [open, preselectedDockId]);

    if (!open) return null;

    const effectiveReason = reason === 'Other' ? customReason.trim() || 'Other' : reason;

    const handleSubmit = async (confirm = false) => {
        if (!startDate || !startTime || !endDate || !endTime) {
            toast.error('All date/time fields are required');
            return;
        }

        // Basic client-side validation using local string comparison (avoids browser timezone bugs)
        const startStr = `${startDate}T${startTime}`;
        const endStr = `${endDate}T${endTime}`;

        if (endStr <= startStr) {
            toast.error('End must be after start');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch('/api/docks/blackouts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    dockId: applyToAll ? null : selectedDockId || null,
                    applyToAllDocks: applyToAll,
                    startDate,
                    startTime,
                    endDate,
                    endTime,
                    reason: effectiveReason,
                    confirm
                }),
            });

            const data = await res.json();

            if (res.status === 409 && data.requiresConfirmation) {
                setConflicts(data.conflicts || []);
                setConflictWarning(data.warning || '');
                setShowConflicts(true);
                setLoading(false);
                return;
            }

            if (!res.ok) throw new Error(data.error || 'Failed to create blackout');

            toast.success('Blackout window created');
            resetAndClose();
            onCreated();
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const resetAndClose = () => {
        setSelectedDockId('');
        setApplyToAll(false);
        setStartDate('');
        setStartTime('08:00');
        setEndDate('');
        setEndTime('17:00');
        setReason('Maintenance');
        setCustomReason('');
        setConflicts([]);
        setShowConflicts(false);
        setConflictWarning('');
        onClose();
    };

    const fmtDT = (iso: string) => {
        const d = new Date(iso);
        return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={resetAndClose} />

            {/* Modal */}
            <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <ShieldOff className="w-5 h-5 text-red-500" />
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white">Block Dock Time</h2>
                    </div>
                    <button onClick={resetAndClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                    {/* Conflict warning */}
                    {showConflicts && (
                        <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 rounded-lg p-4">
                            <div className="flex items-start gap-2 mb-2">
                                <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-bold text-orange-700 dark:text-orange-300">{conflictWarning}</p>
                                    <p className="text-xs text-orange-600/80 dark:text-orange-400/80 mt-1">
                                        Existing appointments will NOT be moved or cancelled.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-3 space-y-1.5">
                                {conflicts.map(c => (
                                    <div key={c.appointmentId} className="flex items-center gap-2 text-xs text-orange-700 dark:text-orange-300 bg-orange-100/60 dark:bg-orange-900/20 rounded px-2.5 py-1.5">
                                        <span className="font-semibold">{c.carrierName || 'Unknown'}</span>
                                        <span className="text-orange-500">·</span>
                                        <span>{c.dockNumber ? `Dock ${c.dockNumber}` : 'No dock'}</span>
                                        <span className="text-orange-500">·</span>
                                        <span className="tabular-nums">{fmtDT(c.startAt)} – {fmtDT(c.endAt)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2 mt-4">
                                <button
                                    onClick={() => setShowConflicts(false)}
                                    className="flex-1 py-2 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    Go Back
                                </button>
                                <button
                                    onClick={() => handleSubmit(true)}
                                    disabled={loading}
                                    className="flex-1 py-2 text-xs font-bold text-white bg-orange-600 hover:bg-orange-500 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                                >
                                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                                    Confirm & Block
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Form — hidden when conflicts are shown */}
                    {!showConflicts && (
                        <>
                            {/* Dock selector */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Dock</label>
                                <select
                                    value={applyToAll ? '__all__' : selectedDockId}
                                    onChange={(e) => {
                                        if (e.target.value === '__all__') {
                                            setApplyToAll(true);
                                            setSelectedDockId('');
                                        } else {
                                            setApplyToAll(false);
                                            setSelectedDockId(e.target.value);
                                        }
                                    }}
                                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer"
                                >
                                    <option value="">Select a dock...</option>
                                    <option value="__all__">🚫 All Docks (Warehouse-Wide)</option>
                                    {docks.map(d => (
                                        <option key={d.id} value={d.id}>Dock {d.dockNumber}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Apply to all checkbox (secondary) */}
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={applyToAll}
                                    onChange={(e) => {
                                        setApplyToAll(e.target.checked);
                                        if (e.target.checked) setSelectedDockId('');
                                    }}
                                    className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-red-600 focus:ring-red-500"
                                />
                                <span className="text-sm text-slate-700 dark:text-slate-300">Apply to all docks</span>
                            </label>

                            {/* Start date/time */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Start Date</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => { setStartDate(e.target.value); if (!endDate) setEndDate(e.target.value); }}
                                        className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Start Time</label>
                                    <input
                                        type="time"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                        className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                </div>
                            </div>

                            {/* End date/time */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">End Date</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">End Time</label>
                                    <input
                                        type="time"
                                        value={endTime}
                                        onChange={(e) => setEndTime(e.target.value)}
                                        className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                </div>
                            </div>

                            {/* Reason */}
                            <div>
                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Reason</label>
                                <select
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer"
                                >
                                    {REASON_PRESETS.map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                                {reason === 'Other' && (
                                    <input
                                        type="text"
                                        value={customReason}
                                        onChange={(e) => setCustomReason(e.target.value)}
                                        placeholder="Enter custom reason..."
                                        className="w-full mt-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={resetAndClose}
                                    className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-white text-sm font-medium rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleSubmit(false)}
                                    disabled={loading || (!applyToAll && !selectedDockId) || !startDate || !endDate}
                                    className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-lg transition-colors shadow-lg shadow-red-500/20 disabled:opacity-50 flex items-center justify-center gap-1.5"
                                >
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                                    Block Time
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
